
import React, { useState, useEffect, useMemo } from "react";
import { message, Button, Modal, Form, Input, Select } from "antd";
import axios from "./Services/Api";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { generateInvoicePDF } from "../Components/generateInvoicePDF";
import { generateDeliveryNotePDF } from "../Components/DeliveryNote";
import "./OrderManagement.css";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { baseURL } from "./api";

dayjs.extend(isBetween);
const OrderManagement = () => {
  const DEFAULT_RESTAURANT_ID = "6809efb2b20b1507f16a52b6";

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editableCart, setEditableCart] = useState([]);
  const [form] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendorNames, setVendorNames] = useState({});
  const [vendorsList, setVendorsList] = useState([]);
  const [stock, setStock] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([
    { label: "Thermocol Box", amount: 0 },
    { label: "Dry Ice", amount: 0 },
  ]);

  // New state for filters
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customRange, setCustomRange] = useState(null);
  const [customRangeVisible, setCustomRangeVisible] = useState(false);

  const statusOptions = [
    { value: "booked", label: "Booked" },
    { value: "accepted", label: "Accepted" },
    { value: "confirmed", label: "Confirmed" },
    { value: "processing", label: "Processing" },
    { value: "packed", label: "Packed" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const round2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

  /* ---------------- initial fetches ---------------- */
  useEffect(() => {
    fetchOrders();
    fetchStock();
  }, []);

  /* ---------------- combined filtering ---------------- */
  useEffect(() => {
    let filtered = orders.filter(
      (o) => !o.priceType || o.priceType === "purchase"
    );

    // Apply search
    if (searchQuery) {
      filtered = filtered.filter((o) =>
        o.orderNumber?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply date filter
    if (dateFilter !== "all") {
      const today = dayjs();
      let startDate, endDate;

      if (dateFilter === "today") {
        startDate = today.startOf("day");
        endDate = today.endOf("day");
      } else if (dateFilter === "last7") {
        startDate = today.subtract(7, "day").startOf("day");
        endDate = today.endOf("day");
      } else if (dateFilter === "lastMonth") {
        startDate = today.subtract(1, "month").startOf("day");
        endDate = today.endOf("day");
      } else if (dateFilter === "custom" && customRange) {
        startDate = customRange[0].startOf("day");
        endDate = customRange[1].endOf("day");
      }

      if (startDate && endDate) {
        filtered = filtered.filter((o) => {
          const orderDate = dayjs(o.date);
          return orderDate.isBetween(startDate, endDate, null, "[]");
        });
      }
    }

    // Apply status filter
    if (statusFilter !== "all") {
      if (statusFilter === "pending") {
        filtered = filtered.filter((o) => o.status !== "delivered");
      } else {
        filtered = filtered.filter((o) => o.status === statusFilter);
      }
    }

    setFilteredOrders(filtered);
  }, [orders, searchQuery, dateFilter, statusFilter, customRange]);

  /* ---------------- helpers ---------------- */
  const fetchOrders = async () => {
    try {
      const { data } = await axios.get("/getAllOrders");
      setOrders(data);
      fetchVendors();
      setLoading(false);
    } catch {
      setError("Failed to fetch orders.");
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const { data } = await axios.get(`${baseURL}/getVendor`);

      // Create a map of vendorId → { name, address }
      const map = {};
      data.forEach((v) => {
        map[v._id] = {
          name: v.vendorName,
          address: v.vendorAddress || "Address not available",
          city: v.city || "",
          state: v.state || "",
        };
      });

      setVendorNames(map);
      setVendorsList(data);
    } catch (err) {
      console.error("Vendor fetch error:", err);
    }
  };

  const fetchStock = async () => {
    try {
      const { data } = await axios.get(
        `/getStock?restaurantId=${DEFAULT_RESTAURANT_ID}`
      );
      setStock(data.stock);
    } catch (err) {
      console.error("Stock fetch error:", err);
    }
  };

  const norm = (id) => (id ? id.toString().trim() : "");

  /* ----------  save order edits ---------- */
  const handleSave = async (values) => {
    try {
      // Preserve original date
      const existingDate = editingOrder?.date || new Date().toISOString();

      // 1️⃣ Update cart totals
      const updatedCart = editableCart.map((it) => ({
        ...it,
        totalPrice: round2(it.price * it.quantity), // inclusive of tax
      }));

      const totalInclusive = updatedCart.reduce(
        (sum, item) => sum + item.totalPrice,
        0
      );

      // 2️⃣ Calculate subtotal & taxes
      // Assuming you have CGST, SGST, IGST rates (from editingOrder)
      const cgstRate = Number(
        editingOrder?.taxes?.find((t) => t.gstType === "CGST")?.rate || 0
      );
      const sgstRate = Number(
        editingOrder?.taxes?.find((t) => t.gstType === "SGST")?.rate || 0
      );
      const igstRate = Number(
        editingOrder?.taxes?.find((t) => t.gstType === "IGST")?.rate || 0
      );

      const subtotal = round2(
        totalInclusive / (1 + (cgstRate + sgstRate + igstRate) / 100)
      );

      const cgstAmount = round2((subtotal * cgstRate) / 100);
      const sgstAmount = round2((subtotal * sgstRate) / 100);
      const igstAmount = round2((subtotal * igstRate) / 100);

      const taxesPayload = [
        { gstType: "CGST", rate: cgstRate, amount: cgstAmount },
        { gstType: "SGST", rate: sgstRate, amount: sgstAmount },
        { gstType: "IGST", rate: igstRate, amount: igstAmount },
      ].filter((t) => t.amount > 0); // only include non-zero taxes

      const additionalTotal = round2(
        additionalCharges.reduce(
          (sum, ch) => sum + (Number(ch.amount) || 0),
          0
        )
      );

      // 3️⃣ Prepare final payload
      const payload = {
        cart: updatedCart,
        subtotal,
        taxes: taxesPayload,
        total: round2(
          subtotal + cgstAmount + sgstAmount + igstAmount + additionalTotal
        ),
        date: existingDate,
        status: values.status,
        additionalCharges,
      };

      // 4️⃣ Update order
      const { data } = await axios.put(
        `/updateOrder/${values.orderNumber}`,
        payload
      );

      // 5️⃣ Update inventory if confirmed & sale
      if (
        values.status === "confirmed" &&
        editingOrder?.priceType === "purchase"
      ) {
        await axios.post("/updateInventoryOnConfirm", {
          orderId: editingOrder._id,
        });
        message.success("Stock reduced from inventory ✅");

        // Refresh stock from backend
        const { data } = await axios.get(
          `/getStock?restaurantId=${DEFAULT_RESTAURANT_ID}`
        );
        setStock(data.stock);
      }

      // 6️⃣ Update frontend state
      setOrders((prev) =>
        prev.map((o) =>
          o.orderNumber === values.orderNumber ? { ...o, ...data } : o
        )
      );

      // 7️⃣ Reset modal
      setEditingOrder(null);
      form.resetFields();
      setEditableCart([]);
      setAdditionalCharges([
        { label: "Thermocol Box", amount: 0 },
        { label: "Dry Ice", amount: 0 },
      ]);

      message.success("Order updated successfully");
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update order.");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    form.setFieldsValue({
      orderNumber: order.orderNumber,
      status: order.status || "booked",
    });
    setEditableCart(order.cart.map((i) => ({ ...i })));
    setAdditionalCharges(
      order.additionalCharges && order.additionalCharges.length
        ? order.additionalCharges
        : [
            { label: "Thermocol Box", amount: 0 },
            { label: "Dry Ice", amount: 0 },
          ]
    );
  };

  const formatDate = (d) => {
    const m = moment(d);
    return m.isValid() ? m.format("MMMM DD, YYYY") : "Invalid Date";
  };

  /* ----------  build stock lookup once per render ---------- */
  const stockMap = useMemo(() => {
    const map = {};
    stock.forEach((s) => {
      const raw =
        s.menuItemId && typeof s.menuItemId === "object"
          ? s.menuItemId._id
          : s.menuItemId;
      map[norm(raw)] = s.inStock;
    });
    return map;
  }, [stock]);

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {/* 🟩 Status Overview Bar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "20px",
          alignItems: "center",
        }}
      >
        {[
          { value: "booked", color: "#faad14", label: "Booked" },
          { value: "accepted", color: "#1890ff", label: "Accepted" },
          { value: "confirmed", color: "#722ed1", label: "Confirmed" },
          { value: "processing", color: "#13c2c2", label: "Processing" },
          { value: "packed", color: "#52c41a", label: "Packed" },
          { value: "shipped", color: "#2f54eb", label: "Shipped" },
          { value: "delivered", color: "#389e0d", label: "Delivered" },
          { value: "cancelled", color: "#f5222d", label: "Cancelled" },
        ].map((status) => {
          const count = orders.filter((o) => o.status === status.value).length;
          return (
            <Button
              key={status.value}
              style={{
                backgroundColor: status.color,
                color: "white",
                border: "none",
                fontWeight: "bold",
              }}
              onClick={() => setStatusFilter(status.value)}
            >
              {status.label} ({count})
            </Button>
          );
        })}

        {/* 🟡 Pending Orders Counter */}
        <Button
          style={{
            backgroundColor: "#fa541c",
            color: "white",
            border: "none",
            fontWeight: "bold",
          }}
          onClick={() => setStatusFilter("pending")}
        >
          Pending Orders (
          {orders.filter((o) => o.status !== "delivered").length})
        </Button>

        {/* 🔁 Reset Filter */}
        <Button
          style={{
            backgroundColor: "#595959",
            color: "white",
            border: "none",
            fontWeight: "bold",
          }}
          onClick={() => {
            setStatusFilter("all");
            setDateFilter("all");
            setCustomRange(null);
            setCustomRangeVisible(false);
          }}
        >
          Show All ({orders.length})
        </Button>
      </div>

      <div
        className="filter-bar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        {/* 🔍 Search Bar */}
        <Input
          placeholder="Search by Order Number"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 250 }}
        />

        {/* 📅 Date Filter */}
        <Select
          value={dateFilter}
          style={{ width: 180 }}
          onChange={(val) => {
            setDateFilter(val);
            setCustomRangeVisible(val === "custom");
            if (val !== "custom") {
              setCustomRange(null);
            }
          }}
        >
          <Select.Option value="all">All Dates</Select.Option>
          <Select.Option value="today">Today</Select.Option>
          <Select.Option value="last7">Last 7 Days</Select.Option>
          <Select.Option value="lastMonth">Last Month</Select.Option>
          <Select.Option value="custom">Custom Range</Select.Option>
        </Select>

        {/* 📆 Custom Range Picker */}
        {customRangeVisible && (
          <DatePicker.RangePicker
            format="YYYY-MM-DD"
            value={customRange}
            onChange={(dates) => {
              setCustomRange(dates);
            }}
          />
        )}

        {/* 🧾 Order Status Filter */}
        <Select
          value={statusFilter}
          style={{ width: 180 }}
          onChange={(value) => setStatusFilter(value)}
        >
          <Select.Option value="all">All Status</Select.Option>
          <Select.Option value="pending">Pending</Select.Option>
          {statusOptions.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      </div>

      {error || filteredOrders.length === 0 ? (
        <div className="no-orders">
          <h3>No Orders Found</h3>
          <p>No orders match your search criteria.</p>
        </div>
      ) : (
        filteredOrders.map((order) => {
          const {
            orderNumber,
            cart = [],
            date,
            status,
            additionalCharges: orderCharges = [],
            taxes = [],
          } = order;

          const displayCharges =
            orderCharges && orderCharges.length
              ? orderCharges
              : [
                  { label: "Thermocol Box", amount: 0 },
                  { label: "Dry Ice", amount: 0 },
                ];

          const vendorData = vendorNames[cart[0]?.vendorId] || {};
          const vendorName = vendorData.name || "Unknown Vendor";
          const vendorAddress = vendorData.address || "";
          const vendorCity = vendorData.city || "";
          const vendorState = vendorData.state || "";

          // Extract tax rates directly from order taxes
          // Ensure taxes is always an array
          const taxesArray = Array.isArray(order.taxes) ? order.taxes : [];

          const cgstTax = taxesArray.find((t) => t.gstType === "CGST") || {
            rate: 0,
          };
          const sgstTax = taxesArray.find((t) => t.gstType === "SGST") || {
            rate: 0,
          };
          const igstTax = taxesArray.find((t) => t.gstType === "IGST") || {
            rate: 0,
          };

          const cgstRate = Number(cgstTax.rate || 0);
          const sgstRate = Number(sgstTax.rate || 0);
          const igstRate = Number(igstTax.rate || 0);

          const totalBeforeTax = round2(
            cart.reduce((sum, item) => {
              const excRate = round2(
                item.price / (1 + (cgstRate + sgstRate + igstRate) / 100)
              );
              return sum + excRate * item.quantity;
            }, 0)
          );

          const cgst = round2((totalBeforeTax * cgstRate) / 100);
          const sgst = round2((totalBeforeTax * sgstRate) / 100);
          const igst = round2((totalBeforeTax * igstRate) / 100);

          const additionalTotal = round2(
            displayCharges.reduce(
              (sum, ch) => sum + (Number(ch.amount) || 0),
              0
            )
          );

          const grandTotal = round2(
            totalBeforeTax + cgst + sgst + igst + additionalTotal
          );
          const statusColors = {
            booked: "#faad14",
            accepted: "#1890ff",
            confirmed: "#722ed1",
            processing: "#13c2c2",
            packed: "#52c41a",
            shipped: "#2f54eb",
            delivered: "#389e0d",
            cancelled: "#f5222d",
          };


          return (
           <div
  key={orderNumber}
  className="order-summary"
  style={{
    position: "relative", // ✅ allows absolute positioning of badge
    border: `3px solid ${statusColors[status] || "#d9d9d9"}`,
    borderRadius: "10px",
    padding: "15px",
    marginBottom: "20px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  }}
>
{/* 🟢 Status Badge */}
              <h3>
                Order Number: {orderNumber}{" "}
                <span style={{ fontSize: 14, color: "#888" }}>({vendorName})📍 {vendorAddress} {vendorCity} {vendorState}</span>
              </h3>
              <p>Order Date: {formatDate(date)}</p>
             <p style={{ position: "relative", display: "inline-block" }}>
  <strong>Delivery Status:</strong>{" "}
  <span
    style={{
      position: "relative",
      top: 0,
      marginLeft: "8px",
      backgroundColor:
        statusColors[status] || "#d9d9d9", // dynamic color
      color: "white",
      fontWeight: "bold",
      padding: "6px 14px",
      borderRadius: "20px",
      fontSize: "13px",
      textTransform: "capitalize",
      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
      display: "inline-block",
      minWidth: "80px",
      textAlign: "center",
    }}
  >
    {status}
  </span>
</p>


              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <h3>Order Summary</h3>
                </AccordionSummary>

                <AccordionDetails>
                  <table className="order-items-table">
                    <thead>
                      <tr>
                        <th>Sr No</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Available Qty</th>
                        <th>Rate (Inc. Tax)</th>
                        <th>Rate (Exc. Tax)</th>
                        <th>Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {cart.map((item, idx) => {
                    const normalizedCartName = item.name.replace(/\s*\(.*\)$/, "").trim();

const matchedStock = stock.find(
  (s) =>
    s.menuItemId?.name?.toLowerCase().trim() === normalizedCartName.toLowerCase()
);

let available = "N/A";
if (matchedStock) {
  const stock1kg = matchedStock.stock1kg ?? 0;
  const stock500gm = matchedStock.stock500gm ?? 0;

  const parts = [];
  if (stock1kg > 0) parts.push(`1 kg: ${stock1kg}`);
  if (stock500gm > 0) parts.push(`500 gm: ${stock500gm}`);

  available = parts.length > 0 ? parts.join(" | ") : "N/A";
}
                        const qty = item.quantity;
                        const incRate = item.price;
                        const excRate = round2(
                          incRate / (1 + (cgstRate + sgstRate + igstRate) / 100)
                        );
                        const amount = round2(qty * excRate);

                        return (
                          <tr key={`${orderNumber}-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>{item.name}</td>
                            <td>{qty}</td>
                           <td>
  {available.split(" | ").map((q, i) => {
    let bg = "#fff7e6", border = "#ffd591", color = "#ad4e00";
    if (q.includes("1 kg")) {
      bg = "#f6ffed"; border = "#b7eb8f"; color = "#237804";
    } else if (q.includes("500 gm")) {
      bg = "#e6f7ff"; border = "#91d5ff"; color = "#0050b3"; 
    }
    return (
      <span
        key={i}
        style={{
          backgroundColor: bg,
          border: `1px solid ${border}`,
          color,
          borderRadius: "12px",
          padding: "2px 8px",
          marginRight: "6px",
          display: "inline-block",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {q}
      </span>
    );
  })}
</td>

                            <td>₹{incRate.toFixed(2)}</td>
                            <td>₹{excRate.toFixed(2)}</td>
                            <td>₹{amount.toFixed(2)}</td>
                          </tr>
                        );
                      })}

                      <tr>
                        <td colSpan="6" style={{ textAlign: "right" }}>
                          <strong>Total Before Tax:</strong>
                        </td>
                        <td>₹{totalBeforeTax.toFixed(2)}</td>
                      </tr>

                      {cgst > 0 && (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "right" }}>
                            <strong>CGST ({cgstRate}%):</strong>
                          </td>
                          <td>₹{cgst.toFixed(2)}</td>
                        </tr>
                      )}
                      {sgst > 0 && (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "right" }}>
                            <strong>SGST ({sgstRate}%):</strong>
                          </td>
                          <td>₹{sgst.toFixed(2)}</td>
                        </tr>
                      )}
                      {igst > 0 && (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "right" }}>
                            <strong>IGST ({igstRate}%):</strong>
                          </td>
                          <td>₹{igst.toFixed(2)}</td>
                        </tr>
                      )}

                      {displayCharges.map((charge, idx) => (
                        <tr key={idx}>
                          <td colSpan="6" style={{ textAlign: "right" }}>
                            <strong>{charge.label}:</strong>
                          </td>
                          <td>₹{Number(charge.amount).toFixed(2)}</td>
                        </tr>
                      ))}

                      <tr>
                        <td colSpan="6" style={{ textAlign: "right" }}>
                          <strong>Grand Total (Incl. Tax):</strong>
                        </td>
                        <td>
                          <strong>₹{grandTotal.toFixed(2)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </AccordionDetails>
              </Accordion>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 10,
                  gap: 10,
                }}
              >
                <Button onClick={() => handleEdit(order)}>Edit Order</Button>
                <Button onClick={() => generateDeliveryNotePDF(order)}>
                  Download Delivery Note
                </Button>
                <Button type="primary" onClick={() => generateInvoicePDF(order)}>
                  Download Bill(PDF)
                </Button>
              </div>
            </div>
          );
        })
      )}

      <Modal
        open={editingOrder !== null}
        onCancel={() => {
          setEditingOrder(null);
          form.resetFields();
          setEditableCart([]);
          setAdditionalCharges([
            { label: "Thermocol Box", amount: 0 },
            { label: "Dry Ice", amount: 0 },
          ]);
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleSave} layout="vertical">
          <Form.Item label="Order Number" name="orderNumber">
            <Input disabled />
          </Form.Item>

          <Form.Item label="Delivery Status" name="status">
            <Select>
              {statusOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <h4>Update Item Quantities</h4>
          {editableCart.map((item, idx) => (
            <div
              key={item._id}
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              <div style={{ flex: 1 }}>{item.name}</div>
              <Input
                type="number"
                min={1}
                value={item.quantity}
                style={{ width: 100, marginLeft: 8 }}
                onChange={(e) => {
                  const newCart = [...editableCart];
                  newCart[idx].quantity = Number(e.target.value);
                  setEditableCart(newCart);
                }}
              />
            </div>
          ))}

          <h4>Additional Charges</h4>
          {additionalCharges.map((charge, idx) => (
            <div
              key={idx}
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              <Input
                placeholder="Charge Label"
                value={charge.label}
                style={{ flex: 1, marginRight: 8 }}
                onChange={(e) => {
                  const newCharges = [...additionalCharges];
                  newCharges[idx].label = e.target.value;
                  setAdditionalCharges(newCharges);
                }}
              />
              <Input
                type="number"
                min={0}
                placeholder="Amount"
                value={charge.amount}
                style={{ width: 120 }}
                onChange={(e) => {
                  const newCharges = [...additionalCharges];
                  newCharges[idx].amount = Number(e.target.value);
                  setAdditionalCharges(newCharges);
                }}
              />
            </div>
          ))}

          <Button
            type="dashed"
            block
            onClick={() =>
              setAdditionalCharges([...additionalCharges, { label: "", amount: 0 }])
            }
            style={{ marginBottom: 16 }}
          >
            + Add More
          </Button>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginTop: 16 }}>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderManagement;
